import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, switchMap, map, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';

import { UserSettingsService } from 'src/app/core/services/user-settings.service';
import { UserSettingsActions } from './user-settings.actions';
import { SnackbarActions } from 'src/app/core/store/snackbar/snackbar.actions';
import { SessionActions } from 'src/app/auth/store/session/session.actions';
import { UserSettingsEntityActions } from '../user-settings-entity/user-settings-entity.actions';
import { selectUserSettings } from './user-settings.selectors';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoadingActions } from 'src/app/core/store/loading/loading.actions';

@Injectable()
export class UserSettingsEffects {
  constructor(
    private actions$: Actions,
    private userSettingsService: UserSettingsService,
    private store: Store,
    private snackBar: MatSnackBar
  ) {}

  setUserSettingsStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserSettingsActions.set_user_settings_start),
        switchMap(data => {
          return this.userSettingsService.setUserSettings(data.userSettings).pipe(
            map(() => UserSettingsActions.set_user_settings_success()),
            catchError(error => of(UserSettingsActions.set_user_settings_fail({ error: error })))
          );
        })
      ),
    { dispatch: true }
  );

  setUserSettingsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserSettingsActions.set_user_settings_success),
        // Get updated user settings, as PUT request does not return any data
        switchMap(() =>
          of(
            UserSettingsEntityActions.get_entity_start(),
            SnackbarActions.snackbar_open({
              snackbarData: { type: 'info', message: 'UI.UserSettings.Message.SavedSuccessfully' }
            }),
            SessionActions.get_user_start(),
            LoadingActions.loading_end()
          )
        )
      ),
    { dispatch: true }
  );

  setUserSettingsFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserSettingsActions.set_user_settings_fail),
        switchMap(data => {
          return [LoadingActions.loading_end(), UserSettingsActions.throw_error({ error: data.error })];
        })
      ),
    { dispatch: true }
  );

  setUserLanguageStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserSettingsActions.set_user_language_start),
        withLatestFrom(this.store.select(selectUserSettings)),
        switchMap(([action, userSettings]) => {
          return this.userSettingsService.setUserSettings(userSettings).pipe(
            map(() => UserSettingsActions.set_user_language_success()),
            catchError(error => of(UserSettingsActions.set_user_language_fail({ error: error })))
          );
        })
      ),
    { dispatch: true }
  );

  setUserLanguageSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserSettingsActions.set_user_language_success),
        // Get updated user settings, as PUT request does not return any data
        switchMap(() => of(UserSettingsEntityActions.get_entity_start(), SessionActions.get_user_start()))
      ),
    { dispatch: true }
  );

  getUserSettingsStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserSettingsActions.get_user_settings_start),
        switchMap(() => {
          return this.userSettingsService.getUserSettings().pipe(
            map(data => UserSettingsActions.get_user_settings_success({ userSettings: data })),
            catchError(error => of(UserSettingsActions.get_user_settings_fail({ error: error })))
          );
        })
      ),
    { dispatch: true }
  );

  getUserSettingsFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserSettingsActions.get_user_settings_fail),
        tap(data => {
          throw data.error;
        })
      ),
    { dispatch: false }
  );

  userSettingsActionsFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserSettingsActions.throw_error),
        tap(data => {
          throw data.error;
        })
      ),
    { dispatch: false }
  );
}
